import { ReactElement, RefCallback, RefObject, useCallback, useRef, useState } from 'react'

import { Transition } from '../Animation/Transition'
import { FADE_TIMEOUT } from '../constants'
import { PickerPlacement, PositionChildProps, TransitionCallback } from '../types'
import { mergeRefs } from '../utils/mergeRefs'
import { PortalContainer } from '../utils/usePortal'
import { useRootClose } from '../utils/useRootClose'
import { Position } from './Position'

const FADE_CLASS_NAME = 'rs-anim-fade'
const FADE_ACTIVE_CLASS_NAME = 'rs-anim-in'

interface OverlayProps {
  open: boolean
  container?: PortalContainer
  placement: PickerPlacement
  preventOverflow: boolean
  triggerTarget: RefObject<HTMLElement>
  onClose: () => void
  onEnter?: TransitionCallback
  onEntered?: TransitionCallback
  onExit?: TransitionCallback
  onExited?: TransitionCallback
  children: (props: PositionChildProps, ref: RefCallback<HTMLElement>) => ReactElement
}

export const Overlay = ({
  open,
  container,
  placement,
  preventOverflow,
  triggerTarget,
  onClose,
  onEnter,
  onEntered,
  onExit,
  onExited,
  children,
}: OverlayProps) => {
  const [exited, setExited] = useState(!open)
  const overlayTarget = useRef<HTMLElement>(null)

  if (open && exited) {
    setExited(false)
  }

  const isMounted = open || !exited

  const handleExited = useCallback(
    (node: HTMLElement | null) => {
      setExited(true)
      onExited?.(node)
    },
    [onExited],
  )

  useRootClose({ onRootClose: onClose, triggerTarget, overlayTarget, disabled: !isMounted })

  if (!isMounted) {
    return null
  }

  return (
    <Transition
      in={open}
      timeout={FADE_TIMEOUT}
      className={FADE_CLASS_NAME}
      activeClassName={FADE_ACTIVE_CLASS_NAME}
      onEnter={onEnter}
      onEntered={onEntered}
      onExit={onExit}
      onExited={handleExited}
    >
      {(transitionProps, transitionRef) => (
        <Position
          ref={transitionRef}
          className={transitionProps.className}
          container={container}
          placement={placement}
          preventOverflow={preventOverflow}
          triggerTarget={triggerTarget}
        >
          {(positionProps, positionRef) =>
            children(positionProps, mergeRefs(positionRef, overlayTarget))
          }
        </Position>
      )}
    </Transition>
  )
}
